import React, { useState } from 'react';
import { gql } from '@apollo/client';

import { RouteComponentProps } from 'react-router';
import { WithApolloClient } from '@apollo/client/react/hoc';
import { useQuery, useMutation } from '@apollo/client';

import { SavingStatus } from 'shared-components/enums';

import { ExtendLock } from 'op-components';
import { LoadingSpinner } from 'shared-components/components';

import withRegistrationForm, { WithRegistrationForm } from '../RegistrationForm';
import RegistrationConflicts from './RegistrationConflicts';

const PATIENT_AND_USER_DETAILS_QUERY = gql`
  query PatientAndUserDetails($patientId: ID!) {
    patient(id: $patientId) {
      id
      lock {
        lockedBy
        readOnly
        lockedByName
      }
      firstName
      middleName
      lastName
      ida
      gender
      dob
      namePrefix
      preferredName
      dobRawDay
      dobRawMonth
      dobRawYear
      primaryPhone
      secondaryPhone
      email
      residentialAddressLine1
      residentialAddressLine2
      residentialAddressCity
      residentialAddressState
      residentialAddressPostcode
      residentialAddressCountry
      postalAddressSameAsResidential
      postalAddressLine1
      postalAddressLine2
      postalAddressCity
      postalAddressState
      postalAddressPostcode
      postalAddressCountry
      address {
        id
        formattedAddress
      }
      attachments {
        id
        filename
        documentType
        filesize
        submitted
        url
        encounterDate
      }
      emergencyContact {
        id
        firstName
        lastName
        relationship
        mobilePhoneNumber
        homePhoneNumber
        email
        authorisedForEnquiries
        supportPerson
      }
      nextOfKinContact {
        id
        firstName
        lastName
        relationship
        mobilePhoneNumber
        homePhoneNumber
        email
        authorisedForEnquiries
        supportPerson
      }
      altContactProvided
      referringFirstName
      referringLastName
      referringPractice
      referringIsInMosaiq
      gpFirstName
      gpLastName
      gpPractice
      gpIsInMosaiq
      referrersOtherSpecialists
      healthMedicareDvaOption
      healthMedicareNumber
      healthMedicareIrn
      healthMedicareExpiry
      healthDvaExpiry
      healthDvaNumber
      healthDvaType
      healthPrivateHealthInsurance
      healthPrivateHealthFundName
      healthPrivateHealthFundNumber
      healthPensionCard
      healthPensionCardNumber
      healthPensionCardExpiry
      countryOfBirth
      languageAtHome
      occupation
      heritage
      maritalStatus
      interpreterRequired
      informationNoticeContent
      informationNoticeAccepted
      additionalComments
      diffMqData
    }

    titleRefData: listData(category: "nameTitle") {
      id
      name
    }
    relationshipsRefData: listData(category: "relationships") {
      id
      name
    }
    heritageRefData: listData(category: "heritage") {
      id
      name
    }
    maritalStatusRefData: listData(category: "maritalStatus") {
      id
      name
    }
    countryOfBirthRefData: listData(category: "countryOfBirth") {
      id
      name
    }
    genderRefData: listData(category: "gender") {
      id
      name
    }
    languageSpokenRefData: listData(category: "languages") {
      id
      name
    }
    medicareTypeRefData: listData(category: "healthFund") {
      id
      name
      appKey
    }
    dvaTypeRefData: listData(category: "dvaCardType") {
      id
      name
      appKey
    }
    ausStateRefData: listData(category: "australiaStates") {
      id
      name
    }
    user {
      id
      isPso
    }
    profile(id: $id) {
      id
      patientFeatures(id: $id) {
        doctorReferralLookupEnabled
      }
    }
  }
`;

const UPDATE_REGO_DATA_CONFLICTS = gql`
  mutation UpdateRegistrationDataConflicts($patientId: ID!, $diffSelectedKey: String!, $diffSelectedValue: String!) {
    updateRegistrationDataConflicts(
      patientId: $patientId
      diffSelectedKey: $diffSelectedKey
      diffSelectedValue: $diffSelectedValue
    ) {
      patient {
        id
        diffMqData
      }
    }
  }
`;

const RESOLVE_PATIENT_CONFLICTS = gql`
  mutation resolveDataConflicts($patientId: ID!) {
    resolvePatientConflicts(patientId: $patientId) {
      patient {
        id
      }
    }
  }
`;

interface Props extends WithApolloClient<{}>, WithRegistrationForm, RouteComponentProps<{ patientId?: string }> {}

const RegistrationConflictsApollo = ({ match, history, showModalIfLocked }: Props): JSX.Element => {
  const [saveStatus, setSaveStatus] = useState('');
  const { patientId } = match.params;

  const { loading, data } = useQuery(PATIENT_AND_USER_DETAILS_QUERY, {
    variables: { patientId },
    fetchPolicy: 'no-cache',
    onCompleted: (data: any): any => {
      showModalIfLocked(data);
      return data;
    },
  });

  const [updateRegoDataConflicts] = useMutation(UPDATE_REGO_DATA_CONFLICTS, {
    onError: (): void => {
      setSaveStatus(SavingStatus.FAILED);
    },
    onCompleted: (data: any): any => {
      showModalIfLocked(data);
      setSaveStatus(SavingStatus.SAVED);
      return data;
    },
  });

  const [resolvePatientConflicts] = useMutation(RESOLVE_PATIENT_CONFLICTS, {
    onCompleted: (): void => {
      history.push({ pathname: `/registration/${patientId}/summary` });
    },
  });

  return (
    <>
      {loading && <LoadingSpinner />}
      {data && (
        <>
          <ExtendLock accessPatientId={data.patient.id} />
          <RegistrationConflicts
            patient={data.patient}
            refData={{
              titleRefData: data.titleRefData,
              relationshipsRefData: data.relationshipsRefData,
              heritageRefData: data.heritageRefData,
              genderRefData: data.genderRefData,
              maritalStatusRefData: data.maritalStatusRefData,
              countryOfBirthRefData: data.countryOfBirthRefData,
              languageSpokenRefData: data.languageSpokenRefData,
              medicareTypeRefData: data.medicareTypeRefData,
              ausStateRefData: data.ausStateRefData,
            }}
            isPso={data.user.isPso}
            updatePatient={updateRegoDataConflicts}
            resolvePatientConflicts={resolvePatientConflicts}
            saveStatus={saveStatus}
          />
        </>
      )}
    </>
  );
};

const apolloComponent = RegistrationConflictsApollo;
const component = withRegistrationForm(apolloComponent);
export default component;
